
import React, { useState, useEffect, useRef } from 'react';
import type { DialogOptions } from '../contexts/DialogContext';

interface DialogModalProps {
  config: DialogOptions;
  onClose: (value: any) => void;
}

const DialogModal: React.FC<DialogModalProps> = ({ config, onClose }) => {
  const [inputValue, setInputValue] = useState(config.defaultValue || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (config.type === 'prompt') {
        // Small timeout to ensure render before focus
        setTimeout(() => {
             if(inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
             }
        }, 10);
    }
  }, [config.type]);

  const handleConfirm = () => {
    if (config.type === 'prompt') {
      onClose(inputValue);
    } else {
      onClose(true);
    }
  };

  const handleCancel = () => {
    if (config.type === 'prompt') {
      onClose(null);
    } else {
      onClose(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
        e.preventDefault(); // Prevent form submission if inside a form
        handleConfirm();
    }
    if (e.key === 'Escape') {
        handleCancel();
    }
  };

  // Ensure z-index is higher than other modals (PrintPreview is z-50, ContentEditor is z-60)
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center z-[200] p-4" onClick={handleCancel}>
      <div 
        className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg shadow-2xl w-full max-w-sm p-6 relative animate-[scale-in_0.2s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {config.title && (
            <h3 className="text-lg font-bold text-[var(--color-text-primary)] mb-2">{config.title}</h3>
        )}
        
        <p className="text-[var(--color-text-secondary)] mb-6 whitespace-pre-wrap">{config.message}</p>
        
        {config.type === 'prompt' && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={config.placeholder}
            onKeyDown={handleKeyDown}
            className="w-full bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-md py-2 px-3 text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] mb-6"
          />
        )}
        
        <div className="flex justify-end gap-3">
          {config.type !== 'alert' && (
            <button 
              onClick={handleCancel}
              className="px-4 py-2 rounded-md font-medium transition-colors bg-transparent hover:bg-white/5 text-[var(--color-text-secondary)] border border-transparent hover:border-[var(--color-border)]"
            >
              {config.cancelText || '취소'}
            </button>
          )}
          <button 
            onClick={handleConfirm}
            className={`px-4 py-2 rounded-md font-bold transition-colors text-white shadow-lg ${
                config.destructive 
                ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-900/20' 
                : 'bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] shadow-black/20'
            }`}
          >
            {config.confirmText || '확인'}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes scale-in {
            0% { opacity: 0; transform: scale(0.95); }
            100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default DialogModal;
